"use strict";
/**
 * Shared tool/product/tracker-name denylist + neutral-placeholder mapping
 * (chore/rec-tool-name-scrub, TASK-1).
 *
 * SINGLE SOURCE OF TRUTH for the deterministic post-processing scrub the
 * bedrock-evaluator runs over finalized `Recommendation` strings (title,
 * description, rootCause, examples) AFTER Haiku consolidation and BEFORE the
 * list is returned/persisted. Backstops the prompt-only tool-neutrality
 * guidance (`buildSystemPrompt()`'s "no tool/product name" clauses) that
 * still leaked a live "bead"/tracker-product token in #584 — this denylist
 * makes the guarantee independent of the LLM.
 *
 * Per the constitution's "shared cross-lambda constants" rule: this lives
 * ONCE here (not a bedrock-evaluator-local literal) so any other Lambda that
 * re-checks recommendation/finding text for the same terms (e.g. a future
 * persister-side or dashboard-side re-validation) imports the SAME list —
 * TypeScript can't catch denylist drift across Lambdas, so co-location is the
 * only guard.
 *
 * Extensible by construction: `BANNED_TOOL_TERMS` is a plain string array —
 * add a term and its scrub call sites pick it up with no shape change.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEUTRAL_TOOL_PLACEHOLDER = exports.TRACKER_CONTEXT_WORDS = exports.CONTEXT_SCOPED_BANNED_TERMS = exports.BANNED_TOOL_TERMS = void 0;
/**
 * Tool/product/tracker nouns that must never appear, verbatim, in a
 * finalized recommendation string. Case-insensitive, word-boundary matched
 * by the consumer (`scrubRecommendations`) — listing the singular/plural
 * forms here keeps the matching regex simple and the list self-documenting.
 *
 * These are matched as BARE words with no surrounding-context requirement,
 * so this list is reserved for terms with no legitimate standalone English
 * usage (unlike `Linear` below, which needs a context-scoped pattern).
 *
 * - `bead` / `beads` — this repo's own internal issue-tracking noun (the
 *   root cause of the #584 leak: "Link each PR to the issue or bead it
 *   closes").
 * - `Jira` — the tracker-product name `buildSystemPrompt()` already
 *   instructs Haiku to avoid naming.
 */
exports.BANNED_TOOL_TERMS = ['bead', 'beads', 'Jira'];
/**
 * Tracker-product names that are ALSO ordinary English words, so a bare
 * word-boundary match would produce false positives (PR #595 review). Each
 * entry is only scrubbed when immediately followed by a tracker-ish noun —
 * matching "Linear issue"/"Linear ticket"/"Linear board" (the tracker-product
 * usage) while leaving "linear history"/"linear regression"/"linear scale"
 * (the ordinary-English usage) untouched. The consumer builds one pattern per
 * entry: `\b<term>\s+(?:issue|ticket|board|task)\b`.
 *
 * - `Linear` — the tracker-product name `buildSystemPrompt()` already
 *   instructs Haiku to avoid naming; unlike `Jira`/`bead`, it collides with
 *   the common adjective ("linear history", "linear regression").
 */
exports.CONTEXT_SCOPED_BANNED_TERMS = ['Linear'];
/** Tracker-ish nouns that, following a context-scoped term, confirm tracker-product usage. */
exports.TRACKER_CONTEXT_WORDS = ['issue', 'ticket', 'board', 'task'];
/**
 * The neutral placeholder every banned term is replaced with. Reads
 * naturally in the linking-key context these terms appear in (a
 * recommendation describing what to cite when linking a PR/commit back to
 * its requirement) — e.g. "Link each PR to the issue or ticket it closes."
 */
exports.NEUTRAL_TOOL_PLACEHOLDER = 'ticket';
//# sourceMappingURL=tool-neutrality.js.map